/**
 * Canonical search field list — JavaScript mirror of
 * tmg-api-functions/search_config.py  SEARCH_FIELDS_CANONICAL["Tools"].
 *
 * KEEP IN SYNC: any edit here must be mirrored in the Python SSOT, and
 * vice-versa. The backend `search_inventory` predicate is authoritative;
 * this list exists for the client-side filtering paths only:
 *   - mobile offline-first surfaces (S8/S9) that filter an offline snapshot
 *   - the sub-min-length (< MIN_QUERY_LENGTH) fallback on back-office
 *     surfaces (S1/S2/S3/S6), which show the full loaded list
 *
 * Field names are the STORED Cosmos names (snake_case). Surfaces that hold
 * tools in a transformed camelCase shape pass a fieldNameMap to
 * matchesCanonicalSearch (see searchFilter.js) — they do NOT fork this list.
 *
 * Source of truth lineage: Session A created search_config.py; Session C
 * mirrors it here; the field set is kept in sync with search_config.py
 * SEARCH_FIELDS_CANONICAL["Tools"].
 */

export const SEARCH_FIELDS_TOOLS = [
  // identifiers + human-facing names
  'supplier_tool_id', 'alt_tool_id',
  'tool_name', 'tool_class', 'tool_code', 'cspc',
  'oem_tool_number', 'list_number',
  'barcode', 'qr_code',
  // categorical / OEM
  'oem', 'tool_type', 'material', 'platform',
  // short identifying text
  'part_description', 'tool_description',
  // location (Tools fields, not LifecycleProfile)
  'site_location', 'detail_location',
  'row_nbr', 'stack_nbr', 'rack_nbr', 'shelf_nbr', 'skid_nbr',
  // supply chain identifiers
  'tier_level', 'tier_duns', 'ship_to_location',
  // status enums
  'workflow_status', 'recon_status', 'supplier_status',
  'preliminary_tool_status',
];

// Minimum query length. Queries shorter than this show the full list
// (client paths return true / "show all"). Parity with the Python SSOT.
export const MIN_QUERY_LENGTH = 2;

/**
 * Canonical field → user-facing match-context label (Session E).
 * Single source of label truth — consumed by the shared matchContext.jsx
 * util AND S1's private computeMatchContext (which may override individual
 * entries; see AssetToolList). The Python backend records only the raw
 * field name in _match_context; labels are a frontend concern.
 * Includes the two child-container pseudo-fields (asset_tag, part_number)
 * so existing behavior is preserved through the same lookup.
 */
export const FIELD_DISPLAY_LABELS = {
  // Identifiers & names
  supplier_tool_id: 'Tool ID',
  alt_tool_id: 'Alt Tool ID',
  tool_name: 'Tool Name',
  tool_class: 'Tool Class',
  tool_code: 'Tool Code',
  cspc: 'CSPC',
  oem_tool_number: 'OEM Tool Number',
  list_number: 'List Number',
  barcode: 'Barcode',
  qr_code: 'QR Code',

  // Categorical / OEM
  oem: 'OEM',
  tool_type: 'Tool Type',
  material: 'Material',
  platform: 'Platform',

  // Short identifying text
  part_description: 'Part Description',
  tool_description: 'Tool Description',

  // Location
  site_location: 'Location',
  detail_location: 'Detail Location',
  row_nbr: 'Row Nbr',
  stack_nbr: 'Stack Nbr',
  rack_nbr: 'Rack Nbr',
  shelf_nbr: 'Shelf Nbr',
  skid_nbr: 'Skid Nbr',

  // Supply chain
  tier_level: 'Tier Level',
  tier_duns: 'Tier DUNS',
  ship_to_location: 'Ship-To Location',

  // Status enums
  workflow_status: 'Workflow Status',
  recon_status: 'Recon Status',
  supplier_status: 'Supplier Status',
  preliminary_tool_status: 'Preliminary Status',

  // Child container fields (preserved)
  asset_tag: 'Asset Tag',
  part_number: 'Part Number',
};

export function getFieldDisplayLabel(field) {
  return FIELD_DISPLAY_LABELS[field] || field; // fallback to raw name
}
